// Consulente taglie NFR — stessa logica del vecchio sito (BMI + altezza + vestibilità).
const SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const STORAGE_KEY = 'nfr_size_profile';

function baseIndex(height, weight) {
  const bmi = weight / Math.pow(height / 100, 2);
  let idx;
  if (bmi < 17) idx = 0;
  else if (bmi < 19) idx = 1;
  else if (bmi < 21.5) idx = 2;
  else if (bmi < 24) idx = 3;
  else if (bmi < 27) idx = 4;
  else if (bmi < 30) idx = 5;
  else if (bmi < 33) idx = 6;
  else idx = 7;
  if (height >= 190) idx = Math.min(idx + 1, SIZES.length - 1);
  else if (height <= 160) idx = Math.max(idx - 1, 0);
  return idx;
}

function suggestSize(height, weight, fit, available) {
  if (!available.length) return null;
  let idx = baseIndex(height, weight);
  if (fit === 'slim') idx = Math.max(idx - 1, 0);
  else if (fit === 'oversized') idx = Math.min(idx + 1, SIZES.length - 1);
  const upper = available.map((s) => s.toUpperCase().trim());
  for (let offset = 0; offset < SIZES.length; offset++) {
    for (const candidate of [SIZES[idx + offset], SIZES[idx - offset]]) {
      if (candidate && upper.includes(candidate)) return available[upper.indexOf(candidate)];
    }
  }
  return available[0];
}

function readProfile() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

function saveProfile(profile) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {}
}

class NfrSizeAdvisor extends HTMLElement {
  #abort = new AbortController();
  #values = { height: 175, weight: 70 };

  connectedCallback() {
    const { signal } = this.#abort;
    this.dialog = this.querySelector('dialog');
    this.sizes = (this.dataset.sizes || '').split('|').filter(Boolean);

    const profile = readProfile();
    if (profile) Object.assign(this.#values, { height: profile.height, weight: profile.weight });
    if (profile?.fit) {
      const fitInput = this.querySelector(`input[value="${profile.fit}"]`);
      if (fitInput) fitInput.checked = true;
    }

    this.querySelectorAll('[data-wheel]').forEach((track) => this.#buildWheel(track, signal));
    this.querySelector('[data-open]').addEventListener('click', () => this.open(), { signal });
    this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close(), { signal });
    this.querySelector('[data-apply]').addEventListener('click', () => this.#apply(), { signal });
    this.querySelectorAll('.nfr-size-advisor__fit input').forEach((input) =>
      input.addEventListener('change', () => this.#update(), { signal })
    );
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    }, { signal });

    if (profile) {
      const size = this.#currentSize();
      if (size) this.querySelector('[data-summary]').textContent = `Per te consigliamo la ${size}`;
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  open() {
    this.dialog.showModal();
    // Centra le rotelle sui valori salvati solo dopo che il dialog è visibile
    requestAnimationFrame(() => {
      this.querySelectorAll('[data-wheel]').forEach((track) => this.#scrollTo(track, this.#values[track.dataset.wheel], false));
      this.#update();
    });
  }

  #buildWheel(track, signal) {
    const { min, max, unit } = track.dataset;
    const items = ['<div class="nfr-wheel__pad"></div>'];
    for (let v = Number(min); v <= Number(max); v++) {
      items.push(`<div class="nfr-wheel__item" role="option" data-value="${v}">${v} ${unit}</div>`);
    }
    items.push('<div class="nfr-wheel__pad"></div>');
    track.innerHTML = items.join('');

    let frame;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const center = track.scrollTop + track.clientHeight / 2;
        let best;
        let bestDistance = Infinity;
        for (const item of track.querySelectorAll('.nfr-wheel__item')) {
          const distance = Math.abs(item.offsetTop + item.offsetHeight / 2 - center);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = item;
          }
        }
        if (!best) return;
        track.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
        best.classList.add('is-active');
        best.setAttribute('aria-selected', 'true');
        this.#values[track.dataset.wheel] = Number(best.dataset.value);
        this.#update();
      });
    }, { signal });

    track.addEventListener('keydown', (event) => {
      const delta = { ArrowUp: -1, ArrowDown: 1 }[event.key];
      if (!delta) return;
      event.preventDefault();
      this.#scrollTo(track, this.#values[track.dataset.wheel] + delta, true);
    }, { signal });
  }

  #scrollTo(track, value, smooth) {
    const item = track.querySelector(`[data-value="${value}"]`);
    if (!item) return;
    track.scrollTo({
      top: item.offsetTop - track.clientHeight / 2 + item.offsetHeight / 2,
      behavior: smooth ? 'smooth' : 'instant',
    });
  }

  #fit() {
    return this.querySelector('.nfr-size-advisor__fit input:checked')?.value || 'regular';
  }

  #currentSize() {
    return suggestSize(this.#values.height, this.#values.weight, this.#fit(), this.sizes);
  }

  #update() {
    const size = this.#currentSize();
    const soldOut = size && isSoldOut(this.dataset.optionName, size);
    this.querySelector('[data-result]').textContent = size ? (soldOut ? `${size} · esaurita` : size) : '—';
    const apply = this.querySelector('[data-apply]');
    apply.textContent = soldOut ? 'Ok, ho capito' : 'Seleziona questa taglia';
  }

  #apply() {
    const size = this.#currentSize();
    saveProfile({ ...this.#values, fit: this.#fit() });
    this.dialog.close();
    if (!size) return;
    // Taglia giusta ma esaurita in questo colore: lo diciamo, senza selezionarla (il bottone diventerebbe "Esaurito")
    if (isSoldOut(this.dataset.optionName, size)) {
      this.querySelector('[data-summary]').textContent = `La tua taglia è la ${size}: al momento è esaurita in questo colore`;
      return;
    }
    this.querySelector('[data-summary]').textContent = `Per te consigliamo la ${size}`;
    selectSize(this.dataset.optionName, size);
  }
}

/** La pagina prodotto barra (classe is-oos) le taglie esaurite nel colore scelto. */
function isSoldOut(optionName, size) {
  const radio = [...document.querySelectorAll('input[type="radio"][data-option-name]')].find(
    (input) => input.dataset.optionName === optionName && input.value === size
  );
  return Boolean(radio?.closest('label')?.classList.contains('is-oos'));
}

function selectSize(optionName, size) {
  const form = document.querySelector('variant-picker, .variant-picker');
  const scope = form || document;
  const radio = [...scope.querySelectorAll('input[type="radio"][data-option-name]')].find(
    (input) => input.dataset.optionName === optionName && input.value === size
  );
  if (radio) {
    radio.click();
    return;
  }
  const select = [...scope.querySelectorAll('select')].find((el) =>
    [...el.options].some((o) => o.dataset.optionName === optionName && o.value === size)
  );
  if (select) {
    select.value = size;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('nfr-size-advisor')) customElements.define('nfr-size-advisor', NfrSizeAdvisor);
